import React from 'react';
import PropTypes from 'prop-types';
import translate from '../../../../../commonComponents/translate';
import { formatNumbersWithZero } from '../../../../../services/utilityFunctions';

function StatCard({ t, label, value, newCount, variant }) {
  return (
    <div className="col-6 col-md-4 col-lg-2">
      <div
        className={`pureliink-stat-card${
          variant ? ` pureliink-stat-card--${variant}` : ''
        }`}
      >
        <div className="pureliink-stat-card__label">{label}</div>
        <div className="pureliink-stat-card__value">{value ?? '-'}</div>
        {newCount != null && (
          <div className="pureliink-stat-card__new">
            +{newCount} {t('pureliink.career.network.new')}
          </div>
        )}
      </div>
    </div>
  );
}

StatCard.propTypes = {
  t: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  newCount: PropTypes.number,
  variant: PropTypes.string,
};

StatCard.defaultProps = {
  value: null,
  newCount: null,
  variant: null,
};

function SubscriptionStatCard({ label, va, count, countLabel, variant }) {
  return (
    <div className="col-6 col-md-4 col-lg-2">
      <div
        className={`pureliink-stat-card${
          variant ? ` pureliink-stat-card--${variant}` : ''
        }`}
      >
        <div className="pureliink-stat-card__label">{label}</div>
        <div className="pureliink-stat-card__value">
          {va != null ? `${formatNumbersWithZero(va, false, true)} VA` : '-'}
        </div>
        {count != null && (
          <div className="pureliink-stat-card__secondary">
            {formatNumbersWithZero(count, false, false)} {countLabel}
          </div>
        )}
      </div>
    </div>
  );
}

SubscriptionStatCard.propTypes = {
  label: PropTypes.string.isRequired,
  countLabel: PropTypes.string,
  va: PropTypes.number,
  count: PropTypes.number,
  variant: PropTypes.string,
};

SubscriptionStatCard.defaultProps = {
  va: null,
  count: null,
  countLabel: '',
  variant: null,
};

function NetworkStats({ t, data }) {
  if (!data) return null;

  const { newRegistrations, subscriptions } = data;

  const fmt = v =>
    v != null ? formatNumbersWithZero(v, false, false) : '-';

  const getTotal = v => (v && typeof v === 'object' ? v.total : v);
  const getNew = v => (v && typeof v === 'object' ? v.new : null);

  const networkItems = [
    {
      label: t('pureliink.career.network.personalClients'),
      value: fmt(getTotal(newRegistrations?.personalClients)),
      newCount: getNew(newRegistrations?.personalClients),
      variant: 'blue',
    },
    {
      label: t('pureliink.career.network.personalCollaborators'),
      value: fmt(getTotal(newRegistrations?.personalCollaborators)),
      newCount: getNew(newRegistrations?.personalCollaborators),
      variant: 'green',
    },
    {
      label: t('pureliink.career.network.personalRecommendations'),
      value: fmt(getTotal(newRegistrations?.personalRecommendations)),
      newCount: getNew(newRegistrations?.personalRecommendations),
      variant: 'amber',
    },
    {
      label: t('pureliink.career.network.groupClients'),
      value: fmt(getTotal(newRegistrations?.groupClients)),
      newCount: getNew(newRegistrations?.groupClients),
      variant: 'pink',
    },
    {
      label: t('pureliink.career.network.groupCollaborators'),
      value: fmt(getTotal(newRegistrations?.groupCollaborators)),
      newCount: getNew(newRegistrations?.groupCollaborators),
      variant: 'purple',
    },
    {
      label: t('pureliink.career.network.groupRecommendations'),
      value: fmt(getTotal(newRegistrations?.groupRecommendations)),
      newCount: getNew(newRegistrations?.groupRecommendations),
      variant: 'cyan',
    },
  ];

  const getVA = v => (v && typeof v === 'object' ? v.va : null);
  const getCount = v => (v && typeof v === 'object' ? v.count : v);

  const subscriptionItems = [
    {
      label: t('pureliink.career.subscriptions.ordersClients'),
      va: getVA(subscriptions?.ordersClients),
      count: getCount(subscriptions?.ordersClients),
      countLabel: t('pureliink.career.subscriptions.orders'),
    },
    {
      label: t('pureliink.career.subscriptions.ordersCollaborators'),
      va: getVA(subscriptions?.ordersCollaborators),
      count: getCount(subscriptions?.ordersCollaborators),
      countLabel: t('pureliink.career.subscriptions.orders'),
    },
    {
      label: t('pureliink.career.subscriptions.subscriptionsClients'),
      va: getVA(subscriptions?.subscriptionsClients),
      count: getCount(subscriptions?.subscriptionsClients),
      countLabel: t('pureliink.career.subscriptions.subscriptionsCount'),
    },
    {
      label: t('pureliink.career.subscriptions.subscriptionsCollaborators'),
      va: getVA(subscriptions?.subscriptionsCollaborators),
      count: getCount(subscriptions?.subscriptionsCollaborators),
      countLabel: t('pureliink.career.subscriptions.subscriptionsCount'),
    },
    {
      label: t('pureliink.career.subscriptions.total'),
      va: getVA(subscriptions?.total),
      count: getCount(subscriptions?.total),
      countLabel: t('pureliink.career.subscriptions.orders'),
      variant: 'highlight',
    },
  ];

  return (
    <>
      <div className="pureliink-stats-section">
        <h4 className="career-section-title">
          {t('pureliink.career.overview.network')}
        </h4>
        <p className="hint-text">
          {t('pureliink.career.network.hint')}
        </p>
        <div className="row">
          {networkItems.map(item => (
            <StatCard
              key={item.label}
              t={t}
              label={item.label}
              value={item.value}
              newCount={item.newCount}
              variant={item.variant}
            />
          ))}
        </div>
      </div>

      <div className="pureliink-stats-section">
        <h4 className="career-section-title">
          {t('pureliink.career.overview.subscriptionsOrders')}
        </h4>
        <p className="hint-text">
          {t('pureliink.career.subscriptions.hint')}
        </p>
        <div className="row">
          {subscriptionItems.map(item => (
            <SubscriptionStatCard
              key={item.label}
              label={item.label}
              va={item.va}
              count={item.count}
              countLabel={item.countLabel}
              variant={item.variant}
            />
          ))}
        </div>
      </div>
    </>
  );
}

NetworkStats.propTypes = {
  t: PropTypes.func.isRequired,
  data: PropTypes.shape({
    newRegistrations: PropTypes.shape({
      personalClients: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ total: PropTypes.number, new: PropTypes.number }),
      ]),
      personalCollaborators: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ total: PropTypes.number, new: PropTypes.number }),
      ]),
      personalRecommendations: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ total: PropTypes.number, new: PropTypes.number }),
      ]),
      groupClients: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ total: PropTypes.number, new: PropTypes.number }),
      ]),
      groupCollaborators: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ total: PropTypes.number, new: PropTypes.number }),
      ]),
      groupRecommendations: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ total: PropTypes.number, new: PropTypes.number }),
      ]),
    }),
    subscriptions: PropTypes.shape({
      ordersClients: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ count: PropTypes.number, va: PropTypes.number }),
      ]),
      ordersCollaborators: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ count: PropTypes.number, va: PropTypes.number }),
      ]),
      subscriptionsClients: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ count: PropTypes.number, va: PropTypes.number }),
      ]),
      subscriptionsCollaborators: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ count: PropTypes.number, va: PropTypes.number }),
      ]),
      total: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ count: PropTypes.number, va: PropTypes.number }),
      ]),
    }),
  }),
};

NetworkStats.defaultProps = {
  data: null,
};

export default translate(NetworkStats);
